import React, { useState, useRef, useEffect } from "react";
import {
  Input,
  Button,
  Avatar,
  Typography,
  Card,
  Space,
  Tooltip,
  Table,
  Image,
  List,
  Divider,
  Modal,
} from "antd";
// @ts-ignore
import ReactMarkdown from 'react-markdown';
// @ts-ignore
import remarkGfm from 'remark-gfm';
// @ts-ignore
import rehypeRaw from 'rehype-raw';
import {
  SendOutlined,
  CloseOutlined,
  MessageOutlined,
  LikeOutlined,
  DislikeOutlined,
  HistoryOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import GenAIService, {
  ChatMessage,
  SessionItem,
  SessionsResponse,
  SessionMessageItem,
  SessionMessagesResponse,
} from "../../../../services/genai/genaiService";
import UserService from "../../../../services/user/userService";
import { userStateSelector } from "../../../../../redux/reducers/user/userReducer";
import AiChatIcon from "../../../../../assets/images/Ai_chat_black.svg";
import ChatEditIcon from "../../../../../assets/images/chat_edit.svg";
import "./styles.less";
import "./privacyModalStyles.less";

const { Text } = Typography;

// Utility function to format relative time
const getRelativeTime = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return diffInMinutes === 1 ? '1 minute ago' : `${diffInMinutes} minutes ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return diffInMonths === 1 ? '1 month ago' : `${diffInMonths} months ago`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return diffInYears === 1 ? '1 year ago' : `${diffInYears} years ago`;
};

interface Message {
  id: string;
  text: string;
  sender: "user" | "assistant";
  timestamp: Date;
  tables?: any[];
  visualizations?: Array<{
    title: string;
    image_base64: string;
  }>;
  request_id?: string;
  session_id?: string;
  feedbackSent?: boolean;
  feedbackRating?: number;
}

interface ChatSession {
  sessionId: string;
  name: string | null;
  createdAt: string;
  updatedAt: string;
  displayTitle?: string; // Computed field for display
  messageCount?: number;
}

interface ChatInterfaceProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  isOpen,
  onClose,
}) => {
  const isFullPage =
    onClose.toString().includes("() => {}") ||
    onClose.toString().includes("()=>{}");
  const { t } = useTranslation();
  const { appUser } = useSelector(userStateSelector);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [dropdownPosition, setDropdownPosition] = useState<{top: number, left: number} | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [sessionToEdit, setSessionToEdit] = useState<string | null>(null);
  const [editSessionName, setEditSessionName] = useState<string>('');
  const [clearAllModalVisible, setClearAllModalVisible] = useState(false);
  const [isCreatingNewSession, setIsCreatingNewSession] = useState(false);
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
  const [showThankYouMessage, setShowThankYouMessage] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<Input>(null);
  const historyButtonRef = useRef<HTMLButtonElement>(null);
  const historyDropdownRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current && !showPrivacyPopup) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, showPrivacyPopup]);

  useEffect(() => {
    // Load chat sessions from API on component mount and auto-load first session
    if (isOpen && appUser?.id) {
      const checkAndLoadChat = async () => {
        try {
          // Check privacy policy consent from API
          const hasConsent = await checkPrivacyPolicyConsent();
          if (!hasConsent) {
            setShowPrivacyPopup(true);
          } else {
            loadChatSessions(true); // Pass true to auto-load first session
          }
        } catch (error) {
          console.error('Error checking privacy consent:', error);
          // Fallback to showing privacy popup on error
          setShowPrivacyPopup(true);
        }
      };

      checkAndLoadChat();
    }
  }, [isOpen, appUser?.id]);

  useEffect(() => {
    // Handle click outside to close history dropdown
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close dropdown if clicking inside modals
      if (deleteModalVisible || editModalVisible || clearAllModalVisible) {
        return;
      }

      // Check if click is on modal backdrop or modal content
      const target = event.target as Element;
      if (target.closest('.ant-modal-root') || target.closest('.ant-modal')) {
        return;
      }

      if (
        isHistoryOpen &&
        historyDropdownRef.current &&
        historyButtonRef.current &&
        !historyDropdownRef.current.contains(event.target as Node) &&
        !historyButtonRef.current.contains(event.target as Node)
      ) {
        setIsHistoryOpen(false);
      }
    };

    if (isHistoryOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isHistoryOpen, deleteModalVisible, editModalVisible, clearAllModalVisible]);

  const loadChatSessions = async (autoLoadFirst: boolean = false) => {
    try {
      const genaiService = new GenAIService();
      const response: SessionsResponse = await genaiService.fetchSessions();
      
      // Transform API response to match our interface and add display titles
      const transformedSessions: ChatSession[] = response.items.map(session => ({
        ...session,
        displayTitle: session.name || formatSessionDate(session.createdAt),
        messageCount: session.messageCount || 0
      }));
      
      setChatSessions(transformedSessions);

      // Auto-load first session messages if requested and sessions exist
      if (autoLoadFirst && transformedSessions.length > 0 && !currentSessionId) {
        const firstSession = transformedSessions[0];
        setCurrentSessionId(firstSession.sessionId);
        await loadSessionMessages(firstSession.sessionId);
      }
    } catch (error) {
      console.error('Failed to load chat sessions:', error);
      // Don't show error to user for now, just log it
    }
  };

  const formatSessionDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      
      // Format as "Chat 12-Aug-2025"
      const day = date.getDate();
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.getFullYear();
      
      return `Chat ${day}-${month}-${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Chat Session';
    }
  };

  const createNewSession = async () => {
    try {
      setIsCreatingNewSession(true);
      setIsHistoryOpen(false);
      setMessages([]);
      setCurrentSessionId(null);

      // Create a local temporary session
      const tempSessionId = `temp-${Date.now()}`;
      const tempSession: ChatSession = {
        sessionId: tempSessionId,
        name: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        displayTitle: formatSessionDate(new Date().toISOString()),
        messageCount: 0
      };

      // Add the temporary session to the local state
      setChatSessions(prev => [tempSession, ...prev]);
      setCurrentSessionId(tempSessionId);
      
      console.log('Created local temporary session:', tempSessionId);
    } catch (error) {
      console.error('Error creating new conversation:', error);
    } finally {
      setIsCreatingNewSession(false);
    }
  };

  const convertApiMessagesToUIMessages = (apiMessages: SessionMessageItem[]): Message[] => {
    const uiMessages: Message[] = [];
    
    // Sort messages by timestamp to ensure correct order
    const sortedMessages = [...apiMessages].sort((a, b) => 
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    
    sortedMessages.forEach((apiMessage) => {
      // Add user message
      const userMessage: Message = {
        id: `user-${apiMessage.requestId}`,
        text: apiMessage.userMessageContent,
        sender: "user",
        timestamp: new Date(apiMessage.userMessageTimestamp),
        request_id: apiMessage.requestId,
        session_id: apiMessage.sessionId,
      };
      uiMessages.push(userMessage);
      
      // Add assistant message - handle new nested format
      const assistantData = apiMessage.assistantResponseMetadata?.data;
      if (assistantData) {
        const assistantMessage: Message = {
          id: `assistant-${apiMessage.requestId}`,
          text: assistantData.text,
          sender: "assistant",
          timestamp: new Date(apiMessage.assistantMessageTimestamp),
          tables: assistantData.tables || [],
          visualizations: assistantData.visualizations || [],
          request_id: apiMessage.requestId,
          session_id: apiMessage.sessionId,
          feedbackSent: apiMessage.hasFeedback || false,
        };
        uiMessages.push(assistantMessage);
      }
    });
    
    return uiMessages;
  };

  const loadSessionMessages = async (sessionId: string) => {
    try {
      const genaiService = new GenAIService();
      const response: SessionMessagesResponse = await genaiService.fetchSessionMessages(sessionId);
      
      // Convert API messages to UI messages format
      const uiMessages = convertApiMessagesToUIMessages(response.items);
      
      setMessages(uiMessages);
    } catch (error) {
      console.error('Failed to load session messages:', error);
      // Show empty messages on error
      setMessages([]);
    }
  };

  const loadSession = async (sessionId: string) => {
    // Load the selected session
    setCurrentSessionId(sessionId);
    setIsHistoryOpen(false);
    
    // Only load messages from API if it's not a temporary session
    if (!sessionId.startsWith('temp-')) {
      await loadSessionMessages(sessionId);
    } else {
      // For temporary sessions, clear messages (they should be empty anyway)
      setMessages([]);
    }
  };

  const showDeleteConfirmation = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSessionToDelete(sessionId);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sessionToDelete) return;

    try {
      // Check if it's a temporary session
      const isTemporarySession = sessionToDelete.startsWith('temp-');
      
      if (!isTemporarySession) {
        // For real sessions, call API to delete
        const genaiService = new GenAIService();
        await genaiService.deleteSession(sessionToDelete);
      }
      
      // Remove from local state (for both temporary and real sessions)
      const updatedSessions = chatSessions.filter(s => s.sessionId !== sessionToDelete);
      setChatSessions(updatedSessions);
      
      // Clear current session if it was deleted
      if (currentSessionId === sessionToDelete) {
        setMessages([]);
        setCurrentSessionId(null);
      }
      
      // Close modal and reset state
      setDeleteModalVisible(false);
      setSessionToDelete(null);
    } catch (error) {
      console.error('Failed to delete session:', error);
      // You could show an error message to the user here
      setDeleteModalVisible(false);
      setSessionToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setSessionToDelete(null);
  };

  const showEditModal = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const session = chatSessions.find(s => s.sessionId === sessionId);
    setSessionToEdit(sessionId);
    setEditSessionName(session?.name || '');
    setEditModalVisible(true);
  };

  const handleEditConfirm = async () => {
    if (!sessionToEdit || !editSessionName.trim()) return;

    try {
      // Check if it's a temporary session
      const isTemporarySession = sessionToEdit.startsWith('temp-');
      
      if (!isTemporarySession) {
        // For real sessions, call API to update name
        const genaiService = new GenAIService();
        await genaiService.updateSessionName(sessionToEdit, editSessionName.trim());
      }
      
      // Update local state (for both temporary and real sessions)
      const updatedSessions = chatSessions.map(s => 
        s.sessionId === sessionToEdit 
          ? { ...s, name: editSessionName.trim(), displayTitle: editSessionName.trim() }
          : s
      );
      setChatSessions(updatedSessions);
      
      // Close modal and reset state
      setEditModalVisible(false);
      setSessionToEdit(null);
      setEditSessionName('');
    } catch (error) {
      console.error('Failed to update session name:', error);
      // You could show an error message to the user here
      setEditModalVisible(false);
      setSessionToEdit(null);
      setEditSessionName('');
    }
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setSessionToEdit(null);
    setEditSessionName('');
  };

  const handlePrivacyAccept = async () => {
    if (!appUser?.id) {
      console.error('No user ID available for privacy policy acceptance');
      return;
    }

    try {
      const userService = new UserService();
      const success = await userService.updateUserChatGPTConsent(appUser.id, true);
      
      if (success) {
        setShowPrivacyPopup(false);
        loadChatSessions(true); // Load sessions after accepting
        console.log('User accepted privacy policy - consent updated on server');
      } else {
        console.error('Failed to update privacy policy consent on server');
        // Optionally show an error message to the user
      }
    } catch (error) {
      console.error('Error updating privacy policy consent:', error);
      // Optionally show an error message to the user
    }
  };

  const handlePrivacyDeny = () => {
    setShowPrivacyPopup(false);
    // Redirect to previous page or close chat
    if (window.history.length > 1) {
      window.history.back();
    } else {
      // If no previous page, close the chat interface
      onClose();
    }
  };

  const openPrivacyPolicy = () => {
    window.open('https://www.bibliotheca.com/privacy-policy/', '_blank');
  };

  const checkPrivacyPolicyConsent = async (): Promise<boolean> => {
    if (!appUser?.id) {
      console.warn('No user ID available for privacy policy check');
      return false;
    }

    try {
      const userService = new UserService();
      const hasConsent = await userService.getUserChatGPTConsent(appUser.id);
      return hasConsent;
    } catch (error) {
      console.error('Error checking privacy policy consent:', error);
      return false;
    }
  };

  const clearAllSessions = () => {
    setClearAllModalVisible(true);
  };

  const handleClearAllConfirm = async () => {
    try {
      // Check if there are any real sessions (non-temporary) to clear from API
      const realSessions = chatSessions.filter(s => !s.sessionId.startsWith('temp-'));
      
      if (realSessions.length > 0) {
        // Call API to clear all real sessions
        const genaiService = new GenAIService();
        await genaiService.clearAllSessions();
      }
      
      // Clear local state (including temporary sessions)
      setChatSessions([]);
      setMessages([]);
      setCurrentSessionId(null);
      setIsHistoryOpen(false);
      setClearAllModalVisible(false);
    } catch (error) {
      console.error('Error clearing all sessions:', error);
      setClearAllModalVisible(false);
    }
  };

  const handleClearAllCancel = () => {
    setClearAllModalVisible(false);
  };

  const calculateDropdownPosition = () => {
    if (historyButtonRef.current) {
      const buttonRect = historyButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 380;
      
      // Position dropdown below the button, aligned to the right
      const top = buttonRect.bottom + 8;
      const left = buttonRect.right - dropdownWidth;
      
      setDropdownPosition({ top, left });
    }
  };

  const toggleHistoryDropdown = () => {
    if (!isHistoryOpen) {
      calculateDropdownPosition();
      setIsHistoryOpen(true);
    } else {
      setIsHistoryOpen(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const currentInputValue = inputValue;
    setInputValue("");
    setIsTyping(true);

    try {
      // Check if this is a temporary session (starts with 'temp-')
      const isTemporarySession = currentSessionId?.startsWith('temp-');
      
      // Use the GenAI service
      const genaiService = new GenAIService();
      const chatMessage: ChatMessage = {
        message: currentInputValue,
        session_id: isTemporarySession ? null : currentSessionId, // Send null for temp sessions
      };

      const data = await genaiService.sendChatMessage(chatMessage);

      if (data.status === "success") {
        // If this was a temporary session, replace it with the real session from API
        if (isTemporarySession && data.session_id) {
          const oldTempSessionId = currentSessionId;
          setCurrentSessionId(data.session_id);
          
          // Remove the temporary session and replace with real session data
          setChatSessions(prev => {
            const filteredSessions = prev.filter(s => s.sessionId !== oldTempSessionId);
            // The real session will be added when we refresh sessions below
            return filteredSessions;
          });
          
          console.log('Converted temporary session to real session:', data.session_id);
        } else if (data.session_id) {
          setCurrentSessionId(data.session_id);
        } else {
          console.log(
            "No session_id received from API, keeping current:",
            currentSessionId
          );
        }

        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: data.data.text,
          sender: "assistant",
          timestamp: new Date(),
          tables: data.data.tables || [],
          visualizations: data.data.visualizations || [],
          request_id: data.request_id,
          session_id: data.session_id,
        };
        setMessages((prev) => [...prev, aiResponse]);

        // Refresh sessions list after successful message (especially for new sessions)
        if (isTemporarySession || !currentSessionId) {
          await loadChatSessions(false);
        }
      } else {
        // Handle error response
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Sorry, I encountered an error. Please try again.",
          sender: "assistant",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, aiResponse]);
      }
    } catch (error) {
      console.error("Chat API error:", error);
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: "Sorry, I encountered an error. Please try again.",
        sender: "assistant",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFeedback = async (messageId: string, rating: number) => {
    const message = messages.find((m) => m.id === messageId);
    if (!message) {
      console.error("Cannot send feedback: message not found");
      return;
    }

    try {
      // Update the message to show feedback was sent
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? { ...m, feedbackSent: true, feedbackRating: rating }
            : m
        )
      );

      // Show thank you message for this specific message
      setShowThankYouMessage(prev => new Set([...prev, messageId]));
      
      // Hide thank you message after 3 seconds
      setTimeout(() => {
        setShowThankYouMessage(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
      }, 3000);

      

      // Call the feedback API if we have the required data
      if (message.request_id && message.session_id) {
        try {
          const genaiService = new GenAIService();
          await genaiService.sendFeedback({
            request_id: message.request_id,
            rating: rating,
            session_id: message.session_id,
          });
          
        } catch (feedbackError) {
          console.error("Failed to send feedback to API:", feedbackError);
          // Optionally revert the UI state if API call fails
          setMessages((prev) =>
            prev.map((m) =>
              m.id === messageId
                ? { ...m, feedbackSent: false, feedbackRating: undefined }
                : m
            )
          );
        }
      } else {
        console.log(
          "Cannot send feedback to API: missing request_id or session_id"
        );
      }
    } catch (error) {
      console.error("Failed to record feedback:", error);
    }
  };

  const renderMessage = (message: Message) => {
    return (
      <div
        key={message.id}
        className={`message ${
          message.sender === "user" ? "user-message" : "assistant-message"
        }`}
      >
        <div className="message-content">
          <div className="message-avatar">
            {/* <Avatar
              size="small"
              style={{
                backgroundColor:
                  message.sender === "user" ? "#1890ff" : "#ffffff",
              }}
            >
              {message.sender === "user" ? (
                "U"
              ) : (
                <img
                  src={AiChatIcon}
                  alt="AI"
                  style={{ width: "16px", height: "16px" }}
                />
              )}
            </Avatar> */}
            {message.sender === "user" ? (
              ""
            ) : (
              <img
                src={AiChatIcon}
                alt="AI"
                style={{ width: "16px", height: "16px" }}
              />
            )}
          </div>
          <div className="message-bubble">
            {message.sender === "assistant" ? (
              <div className="message-text markdown-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                >
                  {message.text}
                </ReactMarkdown>
              </div>
            ) : (
            <Text className="message-text">{message.text}</Text>
            )}

            {/* Render tables if they exist */}
            {message.tables && message.tables.length > 0 && (
              <div className="message-tables">
                {message.tables.map((table, index) => (
                  <div key={index} className="table-container">
                    <Table
                      dataSource={table.rows.map(
                        (row: any[], rowIndex: number) => ({
                          key: rowIndex,
                          ...table.columns.reduce(
                            (acc: any, col: string, colIndex: number) => {
                              acc[col] = row[colIndex];
                              return acc;
                            },
                            {}
                          ),
                        })
                      )}
                      columns={table.columns.map((col: string) => ({
                        title: col,
                        dataIndex: col,
                        key: col,
                      }))}
                      pagination={false}
                      size="small"
                      className="message-table"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Render visualizations if they exist */}
            {message.visualizations && message.visualizations.length > 0 && (
              <div className="message-visualizations">
                {message.visualizations.map((viz, index) => (
                  <div key={index} className="visualization-container">
                    <div className="visualization-title">
                      <Text strong>{viz.title}</Text>
                    </div>
                    <Image
                      src={`data:image/png;base64,${viz.image_base64}`}
                      alt={viz.title}
                      className="message-visualization"
                      //  preview={true}
                    />
                  </div>
                ))}
              </div>
            )}
            {/* Feedback section for assistant messages */}
            {message.sender === "assistant" && (
              <div>
                {!message.feedbackSent && (
                  <div className="message-feedback">
                    <Text
                      type="secondary"
                      style={{ fontSize: "14px", marginRight: "8px" }}
                    >
                      Was this response helpful?
                    </Text>
                    <div className="feedback-buttons">
                      <Button
                        type="text"
                        size="middle"
                        icon={<LikeOutlined />}
                        onClick={() => handleFeedback(message.id, 1)}
                        disabled={message.feedbackSent}
                        className={`feedback-button ${
                          message.feedbackRating === 1 ? "feedback-selected" : ""
                        }`}
                      />
                      <Button
                        type="text"
                        size="middle"
                        icon={<DislikeOutlined />}
                        onClick={() => handleFeedback(message.id, 0)}
                        disabled={message.feedbackSent}
                        className={`feedback-button ${
                          message.feedbackRating === 0 ? "feedback-selected" : ""
                        }`}
                      />
                    </div>
                  </div>
                )}
                {/* Thank you message - shown outside feedbackSent condition */}
                {showThankYouMessage.has(message.id) && (
                  <div className="message-feedback">
                    <Text
                      type="secondary"
                      style={{ fontSize: "11px", marginLeft: "8px" }}
                    >
                      Thank you for your feedback!
                    </Text>
                  </div>
                )}
              </div>
            )}

            {/* Timestamp at the bottom of message bubble */}
            {/* <Text className="message-time" type="secondary">
              {message.timestamp.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Text> */}
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
    <div className={`chat-interface-overlay ${isFullPage ? "full-page" : ""}`}>
      <Card className={`chat-interface-card ${isFullPage ? "full-page" : ""}`}>
        <div className={`chat-header ${isFullPage ? "full-page" : ""}`}>
          <div className="chat-title">
            <MessageOutlined className="chat-icon" />
            <Text strong>Ask me about My Library</Text>
          </div>
            <div className="header-actions">
              <Tooltip title="Chat History">
              <Button
                ref={historyButtonRef}
                type="text"
                icon={<HistoryOutlined />}
                onClick={toggleHistoryDropdown}
                className="history-button"
                size="large"
                disabled={showPrivacyPopup}
              />
              </Tooltip>
          {!isFullPage && (
            <Tooltip title="Close chat">
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={onClose}
                className="close-button"
                size="small"
              />
            </Tooltip>
          )}
            </div>
        </div>

        <div className="chat-messages">
          {messages.length === 0 ? (
            <div className="welcome-message">
              <Text
                type="secondary"
                style={{ fontSize: "16px", textAlign: "center" }}
              >
                Ask a question to get started
              </Text>
            </div>
          ) : (
            <>
              {messages.map(renderMessage)}

              {isTyping && (
                <div className="message assistant-message">
                  <div className="message-content">
                    <div className="message-avatar">
                      {/* <Avatar
                        size="small"
                      >
                      </Avatar> */}
                        <img
                          src={AiChatIcon}
                          alt="AI"
                          style={{ width: "16px", height: "16px" }}
                        />
                    </div>
                    <div className="message-bubble typing">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        <div className={`chat-input ${isFullPage ? "full-page" : ""}`}>
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            className="message-input"
            disabled={showPrivacyPopup}
            suffix={
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isTyping || showPrivacyPopup}
                size="small"
                className="send-button"
              />
            }
          />
        </div>

      </Card>
    </div>

      {/* Chat History Dropdown - Positioned outside the chat card */}
      {isHistoryOpen && (
        <div 
          ref={historyDropdownRef}
          className="chat-history-dropdown"
          style={{
            position: 'fixed',
            top: dropdownPosition?.top || 100,
            left: dropdownPosition?.left || 100,
            zIndex: 10001
          }}
        >
          <Card className="history-dropdown-card">
            <div className="dropdown-header">
              <Text strong style={{ fontSize: '16px' }}>Chat Sessions</Text>
              <Button
                type="text"
                onClick={clearAllSessions}
                size="small"
                danger
                className="clear-all-btn"
                disabled={chatSessions.length === 0}
              >
                Clear All
              </Button>
            </div>

            <div className="session-actions">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={createNewSession}
              block
              className="new-conversation-btn"
              loading={isCreatingNewSession}
              disabled={isCreatingNewSession}
            >
              {isCreatingNewSession ? 'Starting...' : 'Start New Conversation'}
            </Button>
            </div>

            <div className="sessions-header">
              <Text  type="secondary" style={{ fontSize: '14px', color:'black', fontWeight: 'bold' }}>Recent Sessions</Text>
              {/* <Button
                type="text"
                icon={<ReloadOutlined />}
                size="small"
                onClick={() => loadChatSessions(false)}
                title="Refresh sessions"
              /> */}
            </div>

            <div className="sessions-list">
              {chatSessions.length === 0 ? (
                <div className="empty-sessions">
                  <Text type="secondary">No chat sessions yet</Text>
                </div>
              ) : (
                <List
                  dataSource={chatSessions}
                  renderItem={(session) => (
                    <List.Item
                      className={`session-item ${currentSessionId === session.sessionId ? 'active-session' : ''}`}
                      onClick={() => loadSession(session.sessionId)}
                      actions={[
                        <Button
                          type="text"
                          icon={<img src={ChatEditIcon} alt="Edit" style={{ width: "14px", height: "14px" }} />}
                          size="small"
                          onClick={(e) => showEditModal(session.sessionId, e)}
                        />,
                        <Button
                          type="text"
                          icon={<CloseOutlined />}
                          size="small"
                          onClick={(e) => showDeleteConfirmation(session.sessionId, e)}
                        />
                      ]}
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            size="small"
                            style={{ backgroundColor: '#f0f0f0', color: '#666' }}
                          >
                            <MessageOutlined />
                          </Avatar>
                        }
                        title={
                          <Text
                            strong
                            ellipsis
                            className="session-title"
                          >
                            {session.displayTitle}
                          </Text>
                        }
                        description={
                          <div className="session-meta">
                            <Text type="secondary" className="session-time">
                              {session.messageCount} messages , {getRelativeTime(session.createdAt)}
                            </Text>
                            {/* {session.updatedAt !== session.createdAt && (
                              <Text type="secondary" className="session-updated">
                                Updated: {new Date(session.updatedAt).toLocaleDateString()}
                              </Text>
                            )} */}
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
        </div>
      </Card>
    </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        title="Clear Session"
        visible={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Yes, Clear"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        centered
        width={400}
        className="delete-session-modal"
        zIndex={10002}
      >
        <div style={{ padding: '8px 0' }}>
          <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
            Are you sure you want to Clear{' '}
            <strong>
              {sessionToDelete 
                ? chatSessions.find(s => s.sessionId === sessionToDelete)?.displayTitle || 'this chat session'
                : 'this chat session'}
            </strong>?
          </p>
          <p style={{ color: '#8c8c8c', fontSize: '13px', margin: 0, lineHeight: '1.4' }}>
            This action cannot be undone and all messages will be permanently removed.
          </p>
        </div>
      </Modal>

      {/* Edit Session Name Modal */}
      <Modal
        title="Rename Session"
        visible={editModalVisible}
        onOk={handleEditConfirm}
        onCancel={handleEditCancel}
        okText="Rename"
        cancelText="Cancel"
        centered
        width={400}
        className="edit-session-modal"
        zIndex={10002}
      >
        <div style={{ padding: '8px 0' }}>
          <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#595959' }}>
            Enter a new name for this chat session:
          </p>
          <Input
            value={editSessionName}
            onChange={(e) => setEditSessionName(e.target.value)}
            placeholder="Enter session name..."
            maxLength={30}
            autoFocus
            onPressEnter={handleEditConfirm}
          />
          <div style={{ 
            textAlign: 'right', 
            marginTop: '4px', 
            fontSize: '12px', 
            color: '#999' 
          }}>
            {editSessionName.length}/30
          </div>
        </div>
      </Modal>

      {/* Clear All Sessions Confirmation Modal */}
      <Modal
        title="Clear All Sessions"
        visible={clearAllModalVisible}
        onOk={handleClearAllConfirm}
        onCancel={handleClearAllCancel}
        okText="Yes, Clear All"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        centered
        width={400}
        className="clear-all-modal"
        zIndex={10002}
      >
        <div style={{ padding: '8px 0' }}>
          <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
            Are you sure you want to clear all chat sessions?
          </p>
          <p style={{ color: '#8c8c8c', fontSize: '13px', margin: 0, lineHeight: '1.4' }}>
            This action cannot be undone and all sessions with their messages will be permanently removed.
          </p>
        </div>
      </Modal>

      {/* Privacy Policy Modal - Positioned within chat card */}
      {showPrivacyPopup && (
        <div className="privacy-policy-overlay">
          <div className="privacy-policy-card">
            <div className="privacy-policy-header">
              <h3>Privacy Policy Agreement</h3>
            </div>
            <div className="privacy-policy-body">
              <p>
                To use the chat feature, you need to accept our Privacy Policy. 
                This helps us ensure your data is handled securely and in compliance with privacy regulations.
              </p>
              <p>
                Please review our{' '}
                <span 
                  className="privacy-policy-link"
                  onClick={openPrivacyPolicy}
                >
                  Privacy Policy
                </span>
                {' '}before proceeding.
              </p>
            </div>
            <div className="privacy-policy-footer">
              <Button onClick={handlePrivacyDeny}>
                Deny
              </Button>
              <Button type="primary" onClick={handlePrivacyAccept}>
                Accept
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
